/**
 * Track 57P — pure display helpers for the eBA tenant lifecycle panel.
 *
 * The backend (backend/eba2_lifecycle.py) is the sole authority on states,
 * transitions and which actions a user may take (`available_actions`).
 * Nothing here decides permission; it only labels, colours and shapes
 * requests so the panel stays truthful about what the server reported.
 */

export const ACTION_LABELS = {
  provision: "Provision",
  suspend: "Suspend (take offline)",
  resume: "Resume",
  update_entitlements: "Change licences & modules",
  schedule_decommission: "Schedule decommissioning",
  cancel_decommission: "Cancel decommissioning",
  quarantine: "Quarantine now",
  restore: "Restore from quarantine",
  verify_backup: "Verify backup",
  set_legal_hold: "Legal hold",
  approve_deletion: "Approve permanent deletion",
  delete: "Delete permanently",
  reissue_invitation: "Reissue administrator invitation",
  compensate_provisioning: "Remove partial resources",
  mark_cleaned_up: "Close removed test tenant",
  archive_attempt: "Archive test attempt",
  remove_tenant: "Remove tenant",
};

export const ACTION_HELP = {
  provision:
    "Creates the dedicated database, secret reference and migrations, registers the tenant in eBA, " +
    "invites the initial administrator and syncs entitlements. The tenant becomes Active only after verification.",
  suspend: "Users cannot sign in and background work is skipped. All data stays intact.",
  resume: "Lets users sign in again and restarts background work.",
  schedule_decommission:
    "On the chosen date the tenant is quarantined (offline, data retained). You can cancel until then.",
  cancel_decommission: "Returns the tenant to the state it was in before decommissioning was scheduled.",
  quarantine: "Quarantines the tenant immediately instead of waiting for the scheduled date.",
  restore: "Recovers a quarantined tenant to Suspended. Resuming it is a separate, deliberate step.",
  verify_backup:
    "Takes a SQL Server backup on the database server and proves it by restoring it to a scratch database " +
    "(integrity check and comparison), then removes the scratch copy. Required before deletion.",
  set_legal_hold: "While a legal hold is set, the tenant can never be permanently deleted.",
  approve_deletion: "Records your approval for permanent deletion in this decommissioning cycle.",
  delete:
    "Permanently removes ONLY this tenant's dedicated database, secret reference and routing. " +
    "A non-secret audit tombstone is kept. The tenant ID and company code can never be reused. This cannot be undone.",
  compensate_provisioning:
    "Removes ONLY the database, login and secret this failed provisioning created (ownership-tagged), " +
    "then returns the tenant to Draft. Allowed only before the tenant was registered in eBA.",
  reissue_invitation:
    "Sends a fresh invitation to the recorded initial administrator. The reason is kept in the audit timeline.",
  archive_attempt:
    "Hides this concluded Development test attempt from the normal list. Its record, audit timeline and reserved " +
    "identifiers are kept; tick 'Show archived test attempts' to see it again.",
  remove_tenant:
    "Removes this Development tenant completely, in one step: eBA is taken offline and finalized, the database is " +
    "backed up and the backup verified, then the database, both SQL logins, both secrets and any credential setting " +
    "are removed and the removal is verified. Other tenants are not affected and eBA is not restarted. An audit " +
    "tombstone is kept and the tenant ID, company code and database name can never be reused. This cannot be undone.",
  mark_cleaned_up:
    "Use after this Development test tenant's resources were removed. The worker first proves the database, logins, " +
    "secrets, eBA registration and credential setting are all gone; only then is the attempt closed as cleaned up. " +
    "It is never resumed and its identifiers stay reserved — start a new tenant with new identifiers.",
};

// Endpoint path (relative to /eba2/lifecycle/{id}) and HTTP verb per action.
export const ACTION_ENDPOINTS = {
  provision: ["post", "provision"],
  suspend: ["post", "suspend"],
  resume: ["post", "resume"],
  update_entitlements: ["put", "entitlements"],
  schedule_decommission: ["post", "decommission"],
  cancel_decommission: ["post", "decommission/cancel"],
  quarantine: ["post", "quarantine"],
  restore: ["post", "restore"],
  verify_backup: ["post", "verify-backup"],
  set_legal_hold: ["post", "legal-hold"],
  approve_deletion: ["post", "deletion/approve"],
  delete: ["post", "deletion"],
  reissue_invitation: ["post", "administrator-invitation/reissue"],
  compensate_provisioning: ["post", "compensate"],
  mark_cleaned_up: ["post", "mark-cleaned-up"],
  archive_attempt: ["post", "archive"],
  remove_tenant: ["post", "removal"],
};

const CONFIRM_ACTIONS = new Set([
  "schedule_decommission", "quarantine", "restore", "approve_deletion", "delete", "compensate_provisioning",
  "remove_tenant",
]);

export function needsTypedConfirmation(action) {
  return CONFIRM_ACTIONS.has(action);
}

export function typedConfirmationMatches(lifecycle, companyCode, environment) {
  if (!lifecycle) return false;
  const expectedCode = String(lifecycle.company_code || "").trim().toLowerCase();
  const expectedEnv = String(lifecycle.environment_name || "").trim();
  return (
    expectedCode.length > 0 &&
    String(companyCode || "").trim().toLowerCase() === expectedCode &&
    String(environment || "").trim() === expectedEnv
  );
}

/** Badge tone for a lifecycle state: never shows a failure as healthy. */
export function stateTone(lifecycle) {
  if (!lifecycle) return "neutral";
  if (lifecycle.is_failed) return "danger";
  if (lifecycle.is_in_progress) return "progress";
  switch (lifecycle.state) {
    case "active":
      return "success";
    case "suspended":
    case "decommission_scheduled":
    case "quarantined":
      return "warning";
    case "deleted":
      return "muted";
    default:
      return "neutral";
  }
}

export const TONE_CLASSES = {
  danger: "bg-red-50 text-red-800 border-red-300",
  progress: "bg-blue-50 text-blue-800 border-blue-300",
  success: "bg-green-50 text-green-800 border-green-300",
  warning: "bg-amber-50 text-amber-900 border-amber-300",
  muted: "bg-[#F5F5F5] text-[#737373] border-[#D4D4D4]",
  neutral: "bg-white text-[#404040] border-[#D4D4D4]",
};

/** Safe, specific error text from a lifecycle API failure. */
export function describeLifecycleError(err, fallback = "The request failed.") {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object") {
    return {
      message: detail.message || fallback,
      code: detail.code || null,
      reference: detail.reference || null,
    };
  }
  if (typeof detail === "string") return { message: detail, code: null, reference: null };
  if (err?.code === "ECONNABORTED") {
    return { message: "The server took too long to respond. Refresh to check whether it was recorded.", code: "timeout", reference: null };
  }
  return { message: fallback, code: null, reference: null };
}

export function newIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `lk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export function stepUpIsFresh(stepUp, now = Date.now()) {
  if (!stepUp || !stepUp.token || !stepUp.expiresAt) return false;
  // Refresh 30 s early so a slow request never arrives with an expired grant.
  return new Date(stepUp.expiresAt).getTime() - 30000 > now;
}

export const OPERATION_LABELS = {
  provision: "Provisioning",
  suspend: "Suspension",
  resume: "Resumption",
  quarantine: "Quarantine",
  restore: "Restore",
  delete: "Permanent deletion",
  sync_entitlements: "Entitlement sync",
  verify_backup: "Backup verification",
  create_draft: "Draft created",
  update_details: "Details updated",
  update_entitlements: "Entitlements updated",
  schedule_decommission: "Decommission scheduled",
  cancel_decommission: "Decommission cancelled",
  set_legal_hold: "Legal hold changed",
  approve_deletion: "Deletion approved",
  reissue_invitation: "Invitation reissue",
  compensate_provisioning: "Partial-resource removal",
  verify_cleanup: "Clean-up verification",
  mark_cleaned_up: "Clean-up verification requested",
  remove_development_tenant: "Tenant removal",
  remove_tenant: "Tenant removal requested",
};

export const STEP_LABELS = {
  validate_identity: "Validate & reserve identity",
  create_database: "Create dedicated database & login",
  create_secret_reference: "Create Key Vault secret reference",
  run_migrations: "Run migrations",
  register_tenant: "Register tenant in eBA",
  invite_administrator: "Invite initial administrator",
  sync_entitlements: "Sync entitlements to eBA",
  sync_lifecycle_state: "Sync state to eBA",
  verify: "Verify",
  verify_backup: "Verify backup",
  recheck_guards: "Re-check deletion guards",
  finalize_in_eba: "Finalize in eBA (routing removed)",
  delete_secret_reference: "Delete secret reference",
  drop_database: "Drop dedicated database",
  write_tombstone: "Write audit tombstone",
  reissue_administrator_invitation: "Reissue administrator invitation",
  create_database_login: "Create tenant database login",
  drop_database_login: "Drop tenant database login",
  check_compensation: "Check removal is safe",
  initialize_data: "Initial data (empty / test data)",
  verify_cleanup_complete: "Verify nothing is left behind",
  publish_credential_reference: "Hand the credential reference to eBA",
  withdraw_credential_reference: "Withdraw the credential reference from eBA",
};

/** Plain-English worker health line, or null when healthy. */
export function workerHealthMessage(status) {
  if (!status) return null;
  if (!status.workers || status.workers.length === 0) {
    return "The lifecycle worker has never reported in. Requests are recorded but will not run until it is started.";
  }
  if (!status.healthy) {
    return "The lifecycle worker has not reported recently. Requests are recorded and will run when it is back.";
  }
  return null;
}

export const SYNC_LABELS = {
  not_synced: "Not yet synced to eBA",
  pending: "Sync to eBA pending",
  in_sync: "In sync with eBA",
  failed: "Sync to eBA failed",
};

// New-draft defaults (Michael, 2026-10-22): every real tenant buys users and starts with
// Requisitions and Purchase Orders. They are only defaults — the form can change them.
export const DRAFT_DEFAULT_LICENCES = { licensed_main_users: 10, licensed_requesters: 10 };
export const DRAFT_DEFAULT_MODULES = ["requisitions", "orders"];

// The default modules eBA's catalogue offers and allows selecting.
export function defaultDraftModules(catalogModules) {
  const offered = new Set((catalogModules || []).filter((m) => m.selectable).map((m) => m.key));
  return DRAFT_DEFAULT_MODULES.filter((m) => offered.has(m));
}

// A warning, not a refusal: zero licences is allowed (e.g. fixed later) but almost never intended.
export function zeroLicenceWarning(mainUsers, requesters) {
  const zero = [];
  if (!(Number(mainUsers) > 0)) zero.push("main users");
  if (!(Number(requesters) > 0)) zero.push("requesters");
  if (!zero.length) return null;
  return `This tenant will have no licensed ${zero.join(" or ")}. Only the administrator will be able to sign in ` +
    "until licences are added. You can add them later with Update entitlements.";
}

// Base currency (ISO 4217). Chosen while Draft; offered only when eBA's catalogue advertises it —
// otherwise nothing is sent and eBA uses its default, USD.
export const BASE_CURRENCY_DEFAULT = "USD";
export const BASE_CURRENCY_HELP =
  "The tenant's reference currency. Every other currency's rate is stated against it. Most US customers use USD; " +
  "it can be changed later in eBA (System administration › Set base currency) before financial documents exist.";

export function baseCurrencyChoice(catalog) {
  const offer = catalog && catalog.base_currency;
  const supported = Boolean(offer && offer.supported && Array.isArray(offer.options) && offer.options.length);
  return {
    supported,
    default: BASE_CURRENCY_DEFAULT,
    options: supported ? offer.options : [{ code: BASE_CURRENCY_DEFAULT, name: "US Dollar" }],
  };
}

export function baseCurrencyOptionLabel(option, supported = true) {
  if (!supported) return `${BASE_CURRENCY_DEFAULT} (eBA default)`;
  return option.name ? `${option.code} — ${option.name}` : option.code;
}

// The tenant card: a row created before the choice existed (no code) shows eBA's default.
export function baseCurrencyLabel(lifecycle) {
  const bc = lifecycle && lifecycle.base_currency;
  if (!bc || !bc.chosen) return `${BASE_CURRENCY_DEFAULT} (eBA default)`;
  return bc.name ? `${bc.code} — ${bc.name}` : bc.code;
}
